import React, { useState, useCallback } from 'react';
import { observable, toJS } from 'mobx';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';

import {
    FormComputed,
    HiddenField,
    useForm,
    useCalculator,
} from '@ratehub/base-ui';
import {
    RateSelectorCard,
    SCENARIO_TYPES,
    DEFAULT_ALL_PURCHASE_PARAMS,
    DEFAULT_ALL_RENEW_PARAMS,
} from '@ratehub/mtg-common';
import { INTENDED_USE } from '@ratehub/documents/mtg-journey-document/stable';

import DOWNPAYMENT_INSURABILITY_OPTIONS from '../definitions/DownPaymentInsurabilityOptions';


function PaymentCalculatorRateSelector({ column, preferredProvider, ...otherProps }) {
    const localState = useLocalState({ column });

    const calculateMortgageIsInsured = useCallback(
        (values) => getMortgageIsInsured(values, column), 
        [ column ],
    );

    return (
        <>
            {/* The fields are for storing the values from the rate card if the user changes the filters */}
            <HiddenField
                name={`scenarios[${column}].rateTerm`}
                value={localState.rateFilters?.term}
            />

            <HiddenField
                name={`scenarios[${column}].rateType`}
                value={localState.rateFilters?.type}
            />

            <HiddenField
                name={`scenarios[${column}].propertyType`}
                value={localState.rateFilters?.propertyType}
            />

            <FormComputed
                name={`scenarios[${column}].isMortgageInsured`}
                value={calculateMortgageIsInsured}
            />

            <RateSelectorCard
                {...otherProps}
                selectedRateName={`scenarios[${column}].selectedRate`}
                rateFetchingOptions={localState.rateFilters}
                onFilterChange={localState.updateFilters}
                preferredProvider={preferredProvider}
            />
        </>
    );
}

PaymentCalculatorRateSelector.propTypes = {
    column: PropTypes.number.isRequired,
    preferredProvider: PropTypes.string,
    isProductReferralDisabled: PropTypes.bool,
};

PaymentCalculatorRateSelector.defaultProps = {
    preferredProvider: undefined,
    isProductReferralDisabled: undefined,
};

// TODO: We don't really need observable state anymore; we can use Form instead.
function useLocalState({ column }) {
    const form = useForm();
    const calculator = useCalculator();

    const [ state ] = useState(() => observable({
        form: form,
        column: column,

        /**
         * Needed to prevent passing invalid values to rate selector card;
         * If a value is invalid, we want to pass it as null to signal we shouldn't attempt to fetch rates.
         * Only when all values are non-null, we will do a fetch.
         * 
         * @param {string} name 
         * @returns valid value or null
         */
        getFormValue(name) {
            const field = form.getFieldNamed(name);

            // It might be computed or the field isn't in the form (on first render).
            if (!field) {
                return form.getValue(name);
            }

            return field.isValid ? field.typedValue : null;
        },

        get rateFilters() {
            const { scenarioType } = form.values;
            const isPurchaseScenario = scenarioType === SCENARIO_TYPES.PURCHASE;
            const defaultParams = getDefaultParams(scenarioType);

            const savedScenario = state.savedCalculatorData?.payment?.mortgages?.[state.column];
            const savedProperty = state.savedCalculatorData?.property;

            // if document is loaded, we want to load the saved values
            // therefore the order of accessing the filter values is:
            // document > form values > default values
            return {
                ...defaultParams,
                scenario: scenarioType ?? SCENARIO_TYPES.PURCHASE,
                city: getCityObjectFromCityProvincePair(savedProperty?.address)
                    ?? state.getFormValue('location')
                    ?? defaultParams.city,
                amortization: savedScenario?.amortization
                    ? savedScenario.amortization / 12
                    : parseInt(state.getFormValue(`scenarios[${column}].amortization`) ?? DEFAULT_ALL_PURCHASE_PARAMS.amortization),
                type: savedScenario?.type ?? state.getFormValue(`scenarios[${column}.rateType]`) ?? defaultParams.type,
                term: savedScenario?.term ?? parseInt(state.getFormValue(`scenarios[${column}.rateTerm]`) ?? defaultParams.term),
                intendedUse: savedProperty?.intendedUse
                    ?? state.getFormValue(`scenarios[${column}].propertyType`)
                    ?? INTENDED_USE.OWNER_OCCUPIED,
                // isInsured api param only for renewal scenario
                ...!isPurchaseScenario && { isInsured: state.getFormValue(`scenarios[${column}].isMortgageInsured`) },
                // homePrice and downPaymentPercent api param only for purchase scenario
                ...isPurchaseScenario && { homePrice: state.getFormValue('askingPrice') },
                ...isPurchaseScenario && { downPaymentPercent: state.getFormValue(`scenarios[${column}].downPayment`)?.percent != null ? state.getFormValue(`scenarios[${column}].downPayment`).percent / 100 : null },
            };
        },
        get selectedRate() {
            return state.form.values?.scenarios?.[column].selectedRate;
        },
        get isOwnerOccupied() {
            const OWNER_OCCUPIED_OPTIONS = [
                INTENDED_USE.OWNER_OCCUPIED,
                INTENDED_USE.OWNER_OCCUPIED_RENTAL,
            ];

            return OWNER_OCCUPIED_OPTIONS.some(option => option === state.form.values.scenarios?.[column]?.propertyType)
                ? true
                : false;
        },
        get provinceCode() {
            return state.form.values?.location.province.code;
        },
        get isDocumentLoaded() {
            return state.document !== null;
        },
        get savedCalculatorData() {
            return toJS(state.document?.journey?.calculators);
        },
        get document() {
            return calculator.document;
        },
        // Synchronize rate selector modal filters with form values
        updateFilters(values) {
            const { type, term, propertyType } = state.rateFilters;

            const data = {
                ...state.rateFilters, // fields that don't come from rate selector
                type: values?.type ?? type,
                term: values?.term ?? term,
                propertyType: values?.propertyType ?? propertyType,
            };

            // Update hidden fields
            form.setValue(`scenarios[${column}].rateTerm`, data.term);
            form.setValue(`scenarios[${column}].rateType`, data.type);
            form.setValue(`scenarios[${column}].propertyType`, data.propertyType);

            state.document?.journey?.update({
                calculators: {
                    payment: {
                        mortgages: {
                            [state.column]: {
                                type: data.type,
                                term: data.term,
                            },
                        },
                    },
                    property: {
                        intendedUse: data.propertyType,
                    },
                },
            });
        },
    }));

    return state;
}

function getDefaultParams(scenarioType) {
    return scenarioType === SCENARIO_TYPES.PURCHASE
        ? DEFAULT_ALL_PURCHASE_PARAMS
        : DEFAULT_ALL_RENEW_PARAMS;
}

function getCityObjectFromCityProvincePair({ city, province } = {}) {
    if (!city || !province) {
        return null;
    }

    return { name: city, province: { code: province } };
}

function getMortgageIsInsured({ scenarioType, downPaymentPercentage, scenarios }, column) {
    if (scenarioType === SCENARIO_TYPES.PURCHASE) {
        return scenarios[column].downPayment?.percent < 20;
    }

    // Downpayment is a select option in the renewal scenario
    return downPaymentPercentage === DOWNPAYMENT_INSURABILITY_OPTIONS.LESS;
}


export default observer(PaymentCalculatorRateSelector);
